import qs from 'qs';
import { qsOptions } from 'providers/clients/httpClient';
import {
  cleanupAssetFields,
  commonPreviewPayload,
  cleanupRelatedContent,
} from './formatters';
import { isNumber } from 'remeda';
import { DASHBOARD_RESOURCE } from 'screens/dashboard/custom-dashboards/utils/constants';

/**
 * --------------------------------------------------------------------
 * Types and utilities for react admin dataprovider builder calls
 *
 * Call contexts:
 * - getList
 * - getOne
 * - create
 * - update
 * - delete
 * - patch
 * - getMany
 * - getManyReference
 * - updateMany
 * - deleteMany
 * --------------------------------------------------------------------
 */

export type TResourceTarget =
  | 'broadcast/message'
  | 'editorial'
  | 'editorial/templates'
  | 'emailreach/account'
  | 'emailreach/campaign'
  | 'emailreach/contact'
  | 'emailreach/contactlist'
  | 'emailreach/messages'
  | 'emailreach/segment'
  | 'experiments'
  | typeof DASHBOARD_RESOURCE
  | 'savedsearch'
  | 'services'
  | 'subscription/services'
  | 'user'
  | 'user/account/multisite' // Has own DataProvider implementation...
  | 'user/activities'
  | 'wire/channel'
  | 'wire/channel/search'
  | 'wire/driver'
  | 'wire/driver/config'
  | 'wire/result'
  | 'pay/transactions'
  | string; // fallback (throw on unhandled resources)

// IdType is: RA Identifier
export type IdType = string | number;

// SomeRecord is: RaRecord<Identifier>
export type SomeRecord = {
  id: IdType;
  [key: string]: any;
};

// Effectively any object key/string
export type AnyRecordData = {
  [key: string]: any;
};

// Effectively any array
export type AnyArray = Array<any>;

/**
 * This should be used sparingly for edge cases.
 * Certain prebuilt RA components like <EditBase>
 * dont allow passing aditional parameters easily
 * except through react-admin/query's "meta" params
 * object. (eg: WireAssetView.tsx).
 *
 * In most cases we should be able to pass all
 * arguments directly through the "filter" params.
 * If there is a use case please document it here.
 *
 */
export type TMetaParams = {
  queryMeta?: {
    // WireAssetView.tsx
    channel_id?: string;
    [key: string]: any;
  };
  pathMeta?: {
    [key: string]: any;
  };
  bodyMeta?: {
    [key: string]: any;
  };
  [key: string]: any;
};

/**
 * ----------------------------------------------------------
 * Common URL and Payload Building Utilities
 * ----------------------------------------------------------
 */

export type BuildRequestUrlArgs = {
  basePath?: string;
  queryPayload?: AnyRecordData;
};

export type BuildRequestUrlResult = string;

/**
 * Build final URL with query string parameters.
 * Uses qs library to stringify query payload with bracket notation.
 * and is more robust than the older 'query-string' library
 * eg: { previews: { size1:'550w' } } -> 'previews[size1]': '550w'
 */

export function buildRequestUrl({
  basePath,
  queryPayload,
}: BuildRequestUrlArgs): BuildRequestUrlResult {
  try {
    if (!basePath) {
      throw new Error('[buildRequestUrl] Missing basePath.');
    }
    // Fallback to empty obj as payload is optional
    const qpPayloadObj = queryPayload || {};

    const hasQp = Object.keys(qpPayloadObj).length > 0;
    const qpStr = hasQp ? qs.stringify(qpPayloadObj, qsOptions) : '';
    const finalUrl = qpStr ? `${basePath}?${qpStr}` : basePath;

    return finalUrl;
  } catch (error) {
    console.error('[buildRequestUrl]:', error);
    throw error;
  }
}

export type BuildReqBodyArgs = {
  payload?: AnyRecordData | AnyArray | null | undefined;
};

export type BuildReqBodyResult = string | undefined;

/**
 * Stringify body payload for HTTP request.
 * Returns undefined if payload is empty.
 * Axios should handle undefined body appropriately.
 */
export function buildRequestBody({ payload }: BuildReqBodyArgs): BuildReqBodyResult {
  try {
    // Null or undefined: return undefined
    if (payload === null || payload === undefined) {
      return undefined;
    }

    // Arrays: directly stringify or undefined if empty
    // Check array type first as both [] and {} are "object"
    // Generally only patch will use this array case...
    if (Array.isArray(payload)) {
      if (payload.length === 0) {
        return undefined;
      }
      const stringed = JSON.stringify(payload);
      return stringed;
    }

    // Objects: stringify or undefined for empty
    if (typeof payload === 'object') {
      const keys = Object.keys(payload);
      if (keys.length === 0) {
        return undefined;
      }
      const stringed = JSON.stringify(payload);
      return stringed;
    }

    /**
     * Anything else is a likely error (string/number/boolean/etc)
     * so we can throw as payload should be an object/array
     */
    throw new Error('[buildRequestBody] Failed to create body.');
  } catch (error) {
    console.error('[buildRequestBody]:', error);
    throw error;
  }
}

/**
 * Utility for determining unique react keys for items.
 *
 * Determines unique id for REACT KEY / RA ID prop.
 *
 * In most cases used here as to get unique react keys for search
 * results views, for react-admin InfiniteListBase components.
 *
 * In other cases you want to use the getRecordRealId fn instead
 * in SearchSelectProvider to get the id for the item as there
 * are certain workarounds here, eg the user resource concatenation
 * issue is for specifically react keys and not representative
 * of what the actual objects api data id would be.
 *
 * If you need the actual id of the item use getRecordRealId(...)
 * This is primarily for the react key rendering uniqueness.
 *
 */
export function getReactUniqueId(item: any, resource: TResourceTarget) {
  switch (resource) {
    case 'editorial':
    case 'editorial/templates': {
      return item?.uuid;
    }
    case 'user': {
      /**
       * React-Admin Unique Key workaround - concatenate subscription ID if exists.
       * "/user/account/search" could return multiple users with same UUID
       * but different subscription IDs rows, when payload has subscription == true in query
       * or view_by subscription, so concat if exists to get unique.
       */
      const userUuid = item?.uuid || '';
      const subscriptionId = item?.subscription?.id || '';
      const uniqueId = `${userUuid}${subscriptionId}`;
      return uniqueId;
    }
    case 'user/activities': {
      return item?.id;
    }
    case 'savedsearch': {
      return item?.id;
    }
    case 'experiments': {
      return item?.name;
    }
    case 'wire/channel': {
      return item?.id;
    }
    case 'wire/driver': {
      return item?.value;
    }
    case 'wire/channel/search': {
      try {
        const objectId = item?.object_id;
        if (objectId) {
          const parsedObject = JSON.parse(objectId);
          return parsedObject?.external_id || objectId;
        }
        return objectId;
      } catch (e) {
        return item?.object_id;
      }
    }
    case 'emailreach/account': {
      return item?.uuid;
    }
    case 'emailreach/contact': {
      return item?.contact_id;
    }
    case 'emailreach/contactlist': {
      return item?.list_id;
    }
    case 'emailreach/messages': {
      return item?.uuid;
    }
    case 'emailreach/campaign': {
      const getId = item?.uuid || '';
      if (!getId) {
        console.warn('[getReactUniqueId] Missing ID for EmailReach campaign:', item);
      }
      return getId;
    }
    case 'broadcast/message': {
      const id = item?.message_id || '';
      return id;
    }
    case 'broadcast/channel':
    case 'broadcast/account': {
      const id = item?.id || '';
      return id;
    }
    case 'subscription/services':
    case 'services': {
      return item?.id;
    }
    case 'pay/transactions': {
      return item?.id;
    }
    case DASHBOARD_RESOURCE: {
      return item?.id;
    }
    case 'group': {
      return item?.id;
    }
    default: {
      console.warn('[getReactUniqueId] - Defaulted.');
      return item?.uuid || item?.id || undefined;
    }
  }
}

/**
 * --------------------------------------------------------------------
 * Types and utilities for "getList" data provider calls
 * --------------------------------------------------------------------
 */

export type CursorResourceConfig = {
  cursorKey?: string;
};

export const PAGE_CURSOR_CONFIG: CursorResourceConfig = { cursorKey: 'page' };

// Adjusted RA GetListParams Type
export type TGetListParams = {
  filter?: AnyRecordData | undefined;
  meta?: TMetaParams | undefined;
  pagination?: {
    page?: number | string;
    perPage?: number;
  };
  sort?: {
    field?: string;
    order?: string;
  };
};

export type BuildBaseGetListPayloadArgs = {
  // Forwarded all react-admin getList params
  params: TGetListParams;
  // Cursor config object - undefined/default is offset based
  cursorConfig?: CursorResourceConfig;
  // Optional keys to trim (eg: ['query', 'print_slug'])
  trimKeys?: string[];
};

/**
 * Generic helper to craft a getList qp payload
 * for resources that follow standard patterns...
 *
 * Resource specific overrides should be done
 * in the specific getListUtility switch cases
 * after creating this generic base payload.
 */
export function buildBaseGetListPayload({
  params,
  cursorConfig,
  trimKeys = [],
}: BuildBaseGetListPayloadArgs): AnyRecordData {
  try {
    // Nothing to process
    if (!params) {
      return {};
    }

    // Pagination and sorting - via react-admin / react-query
    const page = params?.pagination?.page;
    const perPage = params?.pagination?.perPage;
    const field = params?.sort?.field;
    const order = params?.sort?.order;

    /**
     * Filter object passed through ra lists, params should
     * generally be sent directly through the filter object
     *
     * eg: query, type, all other filters...
     */
    const filter = params?.filter;

    /**
     * Payload notes:
     * - React-Admin lists will automatically default:
     *   to setting 'id' and 'asc' if not set, they can be
     *   removed after creation if needed per resource case.
     * - We generally use uuid for for id sorting, convert ra default here.
     * - Spread filter object last into payload to allow overrides.
     */
    const payload: AnyRecordData = {
      ...(perPage && { limit: perPage }),
      ...(order && { dir: order }),
      ...(field && { sort: field === 'id' ? 'uuid' : field }),
      ...(filter && { ...filter }),
    };

    /**
     * Trim specified keys if they are strings
     */
    if (Array.isArray(trimKeys) && trimKeys.length > 0) {
      for (const key of trimKeys) {
        // key exists in payload and is string
        if (key in payload && typeof payload[key] === 'string') {
          const trimmed = payload[key].trim();
          payload[key] = trimmed;
        }
      }
    }

    /**
     * Handle page paramater:
     * Cursor vs offset pagination
     */

    // Check if a cursor config was specified
    const hasCursorConfig = Boolean(cursorConfig);

    // Cursor config explicitly requested
    if (hasCursorConfig) {
      // Use cursor token if page is a non-empty string
      if (typeof page === 'string' && page.length > 0) {
        const cursorKey = cursorConfig?.cursorKey ?? 'page';
        payload[cursorKey] = page;
      }
      // else: no page set - may be initial request
    }

    // Default: offset-based pagination
    if (!hasCursorConfig) {
      const isValidStringPage = typeof page === 'string' && page.length > 0;
      const isValidPageNumber = isNumber(page) && Number.isFinite(page);
      const canCalculatePage = isValidPageNumber && perPage;

      // Handle both number and string page values
      if (canCalculatePage) {
        // original offset calculation was: (zero based / calculated)
        // first request page comes in as 1 from ra pagination,
        // we convert to 0 based for offset calculation.
        // Then eg: if limit was 50, increments by 50 each time
        // (first req) pg1 = 0, pg2 = 50, pg3 = 100...
        const newPage = (page - 1) * perPage;
        payload.page = newPage;
      } else if (isValidStringPage) {
        // If page was a string use it as is (eg. "17")
        payload.page = page;
      } else {
        // else: no valid page was set (not string or number)
        // may be first request with no page arg sent?
        console.warn('[buildBaseGetListPayload] check page:', page);
      }
    }

    // Return constructed base payload
    return payload;
  } catch (error) {
    console.error('[buildBaseGetListPayload]:', error);
    throw error;
  }
}

/**
 * Allow modification of response data before return.
 *
 * Please use this carefully / sparingly for edge cases.
 * This should only be used if there is absolutely no other
 * way to transform the result data closer to the component
 * or area which is actually invoking / using the response.
 *
 */

export type TGetListResult<RecordType = any> = {
  status: string | number; // not in ra, extended
  data: RecordType[];
  total: number | undefined;
  pageInfo: {
    hasNextPage: boolean | undefined;
    hasPreviousPage: boolean | undefined;
    nextPage: string | number | undefined;
    prevPage: string | number | undefined;
  };
};

/**
 * This is defined as async/await to allow for future
 * use cases that may need to do async operations.
 *
 * Currently just one case needs this:
 * "wire/channel{id}/search/"" as the api returns
 * "results" instead of "items" in the response.
 */
export type TModifyGetListResponseFn<RecordType = any> = (
  response: any,
) => Promise<TGetListResult<RecordType>>;

export type TBuildGetListResult<RecordType = any> = {
  basePath: string;
  queryPayload: AnyRecordData;
  modifyGetListResponse: TModifyGetListResponseFn<RecordType> | undefined;
};

export type TBuildGetListArgs = {
  resource: TResourceTarget;
  params: TGetListParams;
};

export function buildGetListRequest({
  resource,
  params,
}: TBuildGetListArgs): TBuildGetListResult {
  try {
    let basePath = '';
    let modifyGetListResponse: TModifyGetListResponseFn | undefined = undefined;
    let queryPayload: AnyRecordData = {};

    switch (resource) {
      case 'editorial': {
        basePath = '/editorial/asset/search/';
        // CMSADMIN-1049 - trailing space trim
        // trim query: avoid any trailing space issues
        // trim print_slug: api errors if all spaces
        const basePayload = buildBaseGetListPayload({
          params: params,
          trimKeys: ['query', 'print_slug'],
        });
        // Add common preview payload for editorial
        queryPayload = {
          ...commonPreviewPayload,
          ...basePayload,
        };
        break;
      }

      // Templates uses the editorial search endpoint
      // but enforces the template filter setting.
      // Note: by design this is a separate ra resource
      // from "editorial" to allow for separation of concerns
      // which allows for ui/ux adjustments by resource scopes,
      // and separates concerns so "asset search" changes wont
      // accidentally impact the templates view, and vice versa.
      // Allows us to be able to reuse components without pathname checks.
      case 'editorial/templates': {
        basePath = '/editorial/asset/search/';
        const basePayload = buildBaseGetListPayload({
          params: params,
          trimKeys: ['query', 'print_slug'],
        });
        queryPayload = {
          ...commonPreviewPayload,
          ...basePayload,
          template: 'yes',
        };
        break;
      }

      case 'user': {
        basePath = '/user/account/search/';
        // CMSADMIN-1049 - trailing space search issue (enable trim)
        const basePayload = buildBaseGetListPayload({
          params: params,
          trimKeys: ['query'],
        });
        queryPayload = { ...basePayload };
        break;
      }

      case 'group': {
        basePath = '/user/group/';
        const basePayload = buildBaseGetListPayload({
          params: params,
          trimKeys: ['query'],
        });
        queryPayload = { ...basePayload };
        break;
      }

      case 'user/activities': {
        basePath = '/user/activities/search/';
        const basePayload = buildBaseGetListPayload({ params: params });
        // Not supported: remove sort/dir that RA auto-adds
        const { sort, dir, ...restPayload } = basePayload;
        queryPayload = { ...restPayload };
        break;
      }

      case 'savedsearch': {
        basePath = '/core/savedsearch/search/';
        const basePayload = buildBaseGetListPayload({ params: params });
        const { sort, dir, ...restPayload } = basePayload;
        queryPayload = { ...restPayload };
        break;
      }

      case 'experiments': {
        basePath = '/core/experiment/';
        const basePayload = buildBaseGetListPayload({ params: params });
        const { sort, dir, ...restPayload } = basePayload;
        queryPayload = { ...restPayload };
        break;
      }

      case 'wire/channel/search': {
        basePath = `/wire/channel/${params?.filter?.channel}/search/`;
        const basePayload = buildBaseGetListPayload({
          params: params,
          cursorConfig: PAGE_CURSOR_CONFIG,
          trimKeys: ['query', 'raw_query'],
        });
        // Remove channel from payload as it's in the qp path
        const { channel, ...restPayload } = basePayload;
        queryPayload = { ...restPayload };

        /**
         * Syndication endpoint returns 'results' array
         * instead of an 'items' array. This function
         * maps the response to match the standard
         * TGetListResult response structure.
         */
        modifyGetListResponse = async (response) => {
          try {
            if (!response) {
              console.warn(
                '[buildGetListRequest][wire/channel/search][modifyResponse] No response.',
              );
            }

            // Format the same way as other regular getList responses
            const rawItems = response?.data?.results ?? response?.data?.items ?? [];
            const searchItems = Array.isArray(rawItems) ? rawItems : [];
            const dataWithIds = searchItems.map((item: any) => ({
              ...item,
              id: getReactUniqueId(item, resource),
            }));

            const formattedResult: TGetListResult = {
              status: response?.status,
              data: dataWithIds,
              total: response?.data?.total,
              pageInfo: {
                hasNextPage: Boolean(response?.data?.next),
                hasPreviousPage: Boolean(response?.data?.prev),
                nextPage: response?.data?.next,
                prevPage: response?.data?.prev,
              },
            };

            return formattedResult;
          } catch (error) {
            console.error(
              '[buildGetListRequest][wire/channel/search][modifyResponse]:',
              error,
            );
            throw error;
          }
        };

        break;
      }

      case 'wire/channel': {
        // WireSettingsContextProvider targets this
        // directly using a useInfiniteGetList hook.
        basePath = '/wire/channel/';
        const basePayload = buildBaseGetListPayload({ params: params });
        const { sort, ...restPayload } = basePayload;
        queryPayload = { ...restPayload };
        break;
      }

      case 'wire/driver': {
        // WireSettingsContextProvider targets this
        // directly using a useInfiniteGetList hook.
        basePath = '/wire/driver/';
        const basePayload = buildBaseGetListPayload({ params: params });
        queryPayload = { ...basePayload };
        break;
      }

      case 'wire/driver/config': {
        basePath = `/wire/driver/`;
        const basePayload = buildBaseGetListPayload({ params: params });
        queryPayload = { ...basePayload };
        break;
      }

      case 'emailreach/account': {
        basePath = '/emailreach/account/';
        const basePayload = buildBaseGetListPayload({ params: params });
        // Not supported: remove sort/dir that RA auto-adds
        const { sort, dir, ...restPayload } = basePayload;
        queryPayload = { ...restPayload };
        break;
      }

      case 'emailreach/segment': {
        basePath = '/emailreach/segment/';
        const basePayload = buildBaseGetListPayload({ params: params });
        queryPayload = { ...basePayload };
        break;
      }

      case 'emailreach/contact': {
        const accountId = params?.filter?.account_id;
        basePath = `/emailreach/contact/${accountId}/`;
        const basePayload = buildBaseGetListPayload({
          params: params,
          cursorConfig: PAGE_CURSOR_CONFIG,
        });
        // Remove account_id (in path), sort/dir (not supported), and query (rename to search)
        const { account_id, sort, dir, query, lists, ...restPayload } = basePayload;
        queryPayload = {
          ...restPayload,
          // Transform 'query' to 'search'
          ...(query && { search: query }),
          // Handle lists parameter - ensure comma-separated string
          ...(lists && {
            lists: Array.isArray(lists) ? lists.join(',') : lists,
          }),
        };
        break;
      }

      case 'emailreach/contactlist': {
        const accountId = params?.filter?.account_id;
        basePath = `/emailreach/contactlist/${accountId}/`;
        const basePayload = buildBaseGetListPayload({
          params: params,
          cursorConfig: PAGE_CURSOR_CONFIG,
        });
        // Remove account_id (in path), sort/dir (not supported), and query (rename to search)
        const { account_id, sort, dir, query, ...restPayload } = basePayload;
        queryPayload = {
          ...restPayload,
          // Transform 'query' to 'search'
          ...(query && { search: query }),
        };
        break;
      }

      case 'emailreach/messages': {
        const accountId = params?.filter?.account_id;
        basePath = `/emailreach/messages/${accountId}/`;
        const basePayload = buildBaseGetListPayload({
          params: params,
          cursorConfig: PAGE_CURSOR_CONFIG,
        });
        // Remove account_id (in path), sort/dir (not supported), and query (rename to search)
        const { account_id, sort, dir, query, ids, ...restPayload } = basePayload;
        queryPayload = {
          ...restPayload,
          // Transform 'query' to 'search'
          ...(query && { search: query }),
          // Ensure ids is always sent as an array (API expects array format: ids[]=...).
          ...(ids
            ? {
                ids: Array.isArray(ids)
                  ? ids
                  : typeof ids === 'string'
                    ? ids.split(',').filter(Boolean)
                    : [],
              }
            : {}),
        };
        break;
      }

      case 'emailreach/campaign': {
        const accountId = params?.filter?.account_id;
        basePath = `/emailreach/campaign/${accountId}/`;
        const basePayload = buildBaseGetListPayload({
          params: params,
          cursorConfig: PAGE_CURSOR_CONFIG,
        });
        // Remove account_id (in path) and sort/dir (not supported)
        const { account_id, sort, dir, ...restPayload } = basePayload;
        queryPayload = { ...restPayload };
        break;
      }

      case 'subscription/services':
      case 'services': {
        basePath = '/subscription/services/';
        const basePayload = buildBaseGetListPayload({ params });
        // This API uses 'sort_dir' instead of 'dir' (non-standard pattern)
        // TODO: Ask backend to rename this to sort, like the rest of the API.
        // If the payload contains "dir", rename it to "sort_dir", lowercased.
        const { dir, ...restPayload } = basePayload;
        queryPayload = {
          ...restPayload,
          ...(dir && { sort_dir: dir?.toLowerCase() }),
        };
        break;
      }

      case 'pay/transactions': {
        basePath = '/pay/transactions/';
        const basePayload = buildBaseGetListPayload({ params });
        const { dir, ...restPayload } = basePayload;
        queryPayload = {
          ...restPayload,
          ...(dir && { dir: dir?.toLowerCase() }),
        };
        break;
      }

      case 'broadcast/message': {
        basePath = '/broadcast/message/';
        const basePayload = buildBaseGetListPayload({
          params: params,
          trimKeys: ['query'],
        });
        queryPayload = { ...basePayload };
        break;
      }

      case 'broadcast/channel': {
        basePath = '/broadcast/channel/';
        const basePayload = buildBaseGetListPayload({
          params: params,
          trimKeys: ['query'],
        });
        queryPayload = { ...basePayload };
        break;
      }

      case 'broadcast/account': {
        basePath = '/broadcast/account/';
        queryPayload = {};

        /**
         * broadcast/account endpoint returns a flat { status, data: [...] }
         * with no pagination. Map response to standard TGetListResult format.
         */
        modifyGetListResponse = async (response) => {
          try {
            const rawItems = response?.data?.data ?? response?.data ?? [];
            const searchItems = Array.isArray(rawItems) ? rawItems : [];
            const dataWithIds = searchItems.map((item: any) => ({
              ...item,
              id: getReactUniqueId(item, resource),
            }));

            const formattedResult: TGetListResult = {
              status: response?.status,
              data: dataWithIds,
              total: dataWithIds.length,
              pageInfo: {
                hasNextPage: false,
                hasPreviousPage: false,
                nextPage: undefined,
                prevPage: undefined,
              },
            };

            return formattedResult;
          } catch (error) {
            console.error(
              '[buildGetListRequest][broadcast/account][modifyResponse]:',
              error,
            );
            throw error;
          }
        };

        break;
      }

      case DASHBOARD_RESOURCE: {
        basePath = '/insights/dashboard/';
        const basePayload = buildBaseGetListPayload({ params });

        // Rename 'dir' to 'sortDir' and lowercase it
        const { dir, ...restPayload } = basePayload;

        // Special case: convert all array parameters to comma-separated strings
        const processedPayload: AnyRecordData = {};
        for (const [key, value] of Object.entries(restPayload)) {
          if (Array.isArray(value)) {
            processedPayload[key] = value.join(',');
          } else {
            processedPayload[key] = value;
          }
        }

        queryPayload = {
          ...processedPayload,
          ...(dir && {
            sortDir: String(dir).toLowerCase(),
          }),
        };
        break;
      }

      default: {
        throw new Error('[buildGetListRequest] No resource target.');
      }
    }

    return {
      basePath,
      queryPayload,
      modifyGetListResponse,
    };
  } catch (error) {
    console.error('[buildGetListRequest]:', error);
    throw error;
  }
}

/**
 * --------------------------------------------------------------------
 * Types and utilities for "GetOne" data provider calls
 * --------------------------------------------------------------------
 */

export type TGetOneParams = {
  id: IdType;
  meta?: TMetaParams | undefined;
};

export type TGetOneResult<RecordType extends SomeRecord = any> = {
  status: string | number; // not in ra, extended
  id: IdType;
  data: RecordType;
};

export type TBuildGetOneArgs = {
  resource: TResourceTarget;
  params: TGetOneParams;
};

export type TBuildGetOneResult = {
  basePath: string;
  queryPayload: AnyRecordData;
};

/**
 * There are only a few areas that currently use
 * "getOne" calls in the ra data provider, so few
 * resources need to be handled here explicitly.
 *
 * - editorial - asset edit screen
 * - user - user account edit screen
 * - wire/result - wire asset view screen
 * - wire/channel - wire feeds settings  (direct call usage)
 * - wire/driver/config - wire feeds settings (direct call usage)
 *
 * It is better to create dedicated functions where possible,
 * rather than going through the ra data provider calls.
 */
export function buildGetOneRequest({
  resource,
  params,
}: TBuildGetOneArgs): TBuildGetOneResult {
  try {
    let basePath = '';
    let queryPayload: AnyRecordData = {};

    const paramsId = params?.id;
    const validId = paramsId !== null && paramsId !== undefined && paramsId !== '';

    // ID is not null, undefined, or an empty string
    if (!validId) {
      throw new Error('[buildGetOneRequest] Missing valid id.');
    }

    switch (resource) {
      case 'editorial':
      case 'editorial/templates': {
        basePath = `/editorial/asset/${params?.id}/`;
        // Common preview payload for editorial
        queryPayload = {
          ...commonPreviewPayload,
        };
        break;
      }

      case 'user': {
        basePath = `/user/account/${params?.id}/`;
        break;
      }

      // wire/result had no getList in original ra provider..
      // seems used for getOne calls from WireAssetView.tsx...
      // path: /wire/result/ qp: channel_id and object_id
      // object_id is sent in as RA id param here...
      case 'wire/result': {
        basePath = '/wire/result/';
        queryPayload = {
          object_id: `${decodeURIComponent(String(paramsId))}`,
          channel_id: params?.meta?.queryMeta?.channel_id,
        };
        break;
      }

      case 'wire/channel': {
        basePath = `/wire/channel/${params?.id}/`;
        break;
      }

      case 'wire/driver/config': {
        basePath = `/wire/driver/${params?.id}/config/`;
        break;
      }

      case DASHBOARD_RESOURCE: {
        basePath = `/insights/dashboard/${params?.id}/`;
        break;
      }

      default: {
        throw new Error('[buildGetOneRequest] No resource target.');
      }
    }

    return {
      basePath,
      queryPayload,
    };
  } catch (error) {
    console.error('[buildGetOneRequest]:', error);
    throw error;
  }
}

/**
 * --------------------------------------------------------------------
 * Types and utilities for "create" data provider calls
 * --------------------------------------------------------------------
 */

export type TCreateParams = {
  data: AnyRecordData;
  meta?: TMetaParams | undefined;
};

export type TCreateResult<RecordType extends SomeRecord = any> = {
  status: string | number; // not in ra, extended
  id: IdType;
  data: RecordType;
};

export type TBuildCreateReqArgs = {
  resource: TResourceTarget;
  params: TCreateParams;
};

export type TBuildCreateReqResult = {
  basePath: string;
  queryPayload: AnyRecordData;
  bodyPayload: AnyRecordData;
};

/**
 * There are only a few areas that currently use
 * "create" calls in the ra data provider, so few
 * resources need to be handled here explicitly.
 *
 * - user - user account (UserCreateForm.tsx)
 * - editorial - editorial asset (RightSide.tsx)
 * - wire/channel - WireSettingsContextProvider (direct call usage)
 *
 * It is better to create dedicated functions where possible,
 * rather than going through the ra data provider calls.
 */

export function buildCreateRequest({
  resource,
  params,
}: TBuildCreateReqArgs): TBuildCreateReqResult {
  try {
    let basePath = '';
    let queryPayload: AnyRecordData = {};
    let bodyPayload: AnyRecordData = {};

    const payloadData = params?.data || {};

    switch (resource) {
      case 'editorial':
      case 'editorial/templates': {
        basePath = '/editorial/asset/';
        // Add common preview payload for editorial
        const basePayload = {
          ...commonPreviewPayload,
          ...payloadData,
        };
        // Cleanup related content, strip attrs off tiptap html
        const relatedPayload = cleanupRelatedContent(basePayload);
        const finalPayload = cleanupAssetFields(relatedPayload);
        bodyPayload = { ...finalPayload };
        break;
      }
      case 'user': {
        basePath = '/user/account/';
        bodyPayload = { ...payloadData };
        break;
      }
      case 'wire/channel': {
        basePath = '/wire/channel/';
        bodyPayload = { ...payloadData };
        break;
      }
      default: {
        throw new Error('[buildCreateRequest] No resource target.');
      }
    }

    return {
      basePath,
      queryPayload,
      bodyPayload,
    };
  } catch (error) {
    console.error('[buildCreateRequest]:', error);
    throw error;
  }
}

/**
 * --------------------------------------------------------------------
 * Types and utilities for "update" data provider calls
 * --------------------------------------------------------------------
 */

export type TUpdateParams = {
  id: IdType;
  data: AnyRecordData;
  meta?: TMetaParams | undefined;
  previousData: AnyRecordData;
};

export type TUpdateResult<RecordType extends SomeRecord = any> = {
  status: string | number; // not in ra, extended
  id: IdType;
  data: RecordType;
};

export type TBuildUpdateReqArgs = {
  resource: TResourceTarget;
  params: TUpdateParams;
};

export type TBuildUpdateReqResult = {
  basePath: string;
  queryPayload: AnyRecordData;
  bodyPayload: AnyRecordData;
};

/**
 * There are only a few areas that currently use
 * "update" calls in the ra data provider, so few
 * resources need to be handled here explicitly.
 *
 * - 'user' resource (via useUpdate hook):
 * -- UserEdit.tsx - profile edit screen
 *
 * - 'editorial' resource (via useUpdate hook):
 * -- RightSide.tsx, RevisionsContext.tsx
 *
 * -  WireSettingsContextProvider (wire context):
 * -- wire/channel: direct call - in handleChannelEdit
 *
 * - CampaignEditForm.tsx (emailreach campaigns):
 * -- emailreach/campaign: direct call - update mutation
 *
 * It is better to create dedicated functions where possible,
 * rather than going through the ra data provider calls.
 */

export function buildUpdateRequest({
  resource,
  params,
}: TBuildUpdateReqArgs): TBuildUpdateReqResult {
  try {
    let basePath = '';
    let queryPayload: AnyRecordData = {};
    let bodyPayload: AnyRecordData = {};

    const paramsId = params?.id;
    const validId = paramsId !== null && paramsId !== undefined && paramsId !== '';

    // ID is not null, undefined, or an empty string
    if (!validId) {
      throw new Error('[buildUpdateRequest] Missing valid id.');
    }

    const payloadData = params?.data || {};

    switch (resource) {
      case 'editorial':
      case 'editorial/templates': {
        basePath = `/editorial/asset/${paramsId}/`;
        // Add common preview payload for editorial
        const basePayload = {
          ...commonPreviewPayload,
          ...payloadData,
        };
        // Cleanup related content, strip attrs off tiptap html
        const relatedPayload = cleanupRelatedContent(basePayload);
        const finalPayload = cleanupAssetFields(relatedPayload);
        bodyPayload = { ...finalPayload };
        break;
      }

      case 'user': {
        basePath = `/user/account/${paramsId}/`;
        bodyPayload = { ...payloadData };
        break;
      }

      case 'wire/channel': {
        basePath = `/wire/channel/${paramsId}/`;
        bodyPayload = { ...payloadData };
        break;
      }

      case 'emailreach/campaign': {
        basePath = `/emailreach/campaign/${paramsId}/`;
        bodyPayload = { ...payloadData };
        break;
      }

      default: {
        throw new Error('[buildUpdateRequest] No resource target.');
      }
    }

    return {
      basePath,
      queryPayload,
      bodyPayload,
    };
  } catch (error) {
    console.error('[buildUpdateRequest]:', error);
    throw error;
  }
}

/**
 * --------------------------------------------------------------------
 * Types and utilities for "delete" data provider calls
 * --------------------------------------------------------------------
 */

export type TDeleteParams = {
  id: IdType;
  meta?: TMetaParams | undefined;
  previousData?: AnyRecordData; // ra type defines this
  data?: AnyRecordData; // not in ra, extended
};

export type TDeleteResult<RecordType extends SomeRecord = any> = {
  status: string | number; // not in ra, extended
  data: RecordType; // ra type defines this
};

export type TBuildDeleteReqArgs = {
  resource: TResourceTarget;
  params: TDeleteParams;
};

export type TBuildDeleteReqResult = {
  basePath: string;
  queryPayload: AnyRecordData;
};

/**
 * There are only a few areas that currently use
 * "delete" calls in the ra data provider, so few
 * resources need to be handled here explicitly.
 *
 * -  WireSettingsContextProvider (wire context):
 * -- wire/channel (direct call - in handleChannelDelete)
 *
 * It is better to create dedicated functions where possible,
 * rather than going through the ra data provider calls.
 */

export function buildDeleteRequest({
  resource,
  params,
}: TBuildDeleteReqArgs): TBuildDeleteReqResult {
  try {
    let basePath = '';
    let queryPayload: AnyRecordData = {};

    const paramsId = params?.id;
    const validId = paramsId !== null && paramsId !== undefined && paramsId !== '';

    // ID is not null, undefined, or an empty string
    if (!validId) {
      throw new Error('[buildDeleteRequest] Missing valid id.');
    }

    switch (resource) {
      case 'wire/channel': {
        basePath = `/wire/channel/${paramsId}/`;
        break;
      }
      default: {
        throw new Error('[buildDeleteRequest] No resource target.');
      }
    }

    return {
      basePath,
      queryPayload,
    };
  } catch (error) {
    console.error('[buildDeleteRequest]:', error);
    throw error;
  }
}

/**
 * --------------------------------------------------------------------
 * Types and utilities for "patch" data provider calls
 * --------------------------------------------------------------------
 */

// see: components['schemas']['core.patch_list'] (PatchType)
type TPatches = {
  /** Format: json-pointer - identifying the target of the operation */
  path: string;
  /** Operation to perform */
  op: 'add' | 'replace' | 'test' | 'remove' | string;
  /** Value to apply for `add`, `replace, or `test` operations */
  value?: string[] | string | number | boolean;
  [key: string]: any;
};

export type TPatchParams = {
  id: IdType;
  data: TPatches[];
  meta?: TMetaParams | undefined;
};

export type TPatchResult<RecordType extends SomeRecord = any> = {
  status: string | number;
  data: RecordType;
  id: IdType;
};

export type TBuildPatchReqArgs = {
  resource: TResourceTarget;
  params: TPatchParams;
};

export type TBuildPatchReqResult = {
  basePath: string;
  patchArray: TPatches[];
  queryPayload: AnyRecordData;
};

/**
 * There may only be a few areas that currently use
 * "patch" calls in the ra data provider, so few
 * resources need to be handled here explicitly.
 *
 * It is better to create dedicated functions where possible,
 * rather than going through the ra data provider calls.
 */

export function buildPatchRequest({
  resource,
  params,
}: TBuildPatchReqArgs): TBuildPatchReqResult {
  try {
    let basePath = '';
    let queryPayload: AnyRecordData = {};
    let patchArray: TPatches[] = [];

    const paramsId = params?.id;
    const validId = paramsId !== null && paramsId !== undefined && paramsId !== '';

    // ID is not null, undefined, or an empty string
    if (!validId) {
      throw new Error('[buildPatchRequest] Missing valid id.');
    }

    /**
     * Patch operations data array of patch objects
     * [ {"path": "/a/b/c","op": "add","value": "string"}, ... ]
     */
    const patches = params?.data || [];

    // Patches must be an array
    if (!Array.isArray(patches) || patches.length === 0) {
      throw new Error('[buildPatchRequest] Missing patches array.');
    }

    switch (resource) {
      case 'editorial': {
        /**
         * Note: most editorial resources have been swapped
         * directly to use patchEditorialAsset function,
         * this is here just in case any were missed.
         */
        basePath = `/editorial/asset/${paramsId}/`;
        patchArray = [...patches];
        break;
      }
      default: {
        throw new Error('[buildPatchRequest] No resource target.');
      }
    }

    return {
      basePath,
      queryPayload,
      patchArray,
    };
  } catch (error) {
    console.error('[buildPatchRequest]:', error);
    throw error;
  }
}

/**
 * --------------------------------------------------------------------
 * Types and utilities for "deleteMany" data provider calls
 * Note: Currently no resources use deleteMany via ra data provider.
 * --------------------------------------------------------------------
 */

export type TDeleteManyParams = {
  ids: IdType[];
  meta?: TMetaParams | undefined;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export type TDeleteManyResult<RecordType extends SomeRecord = SomeRecord> = {
  status: string | number; // not in ra, extended
  data: IdType[]; // ra type defines this
};

export type TBuildDeleteManyRequest = {
  resource: TResourceTarget;
  params: TDeleteManyParams;
};

export type TBuildDeleteManyReqResult = {
  basePath: string;
  queryPayload: AnyRecordData;
  bodyPayload: AnyRecordData;
};

export function buildDeleteManyRequest({
  resource,
  params,
}: TBuildDeleteManyRequest): TBuildDeleteManyReqResult {
  try {
    let basePath = '';
    let queryPayload: AnyRecordData = {};
    let bodyPayload: AnyRecordData = {};

    const paramsIds = params?.ids;
    const validIds = Array.isArray(paramsIds) && paramsIds.length > 0;

    // IDs is a non-empty array
    if (!validIds) {
      throw new Error('[buildDeleteManyRequest] Missing ids array.');
    }

    switch (resource) {
      // Add resource cases as needed
      // case 'some/resource': {
      //   basePath = '/some/resource/delete';
      //   bodyPayload = { ids: params.ids };
      //   break;
      // }
      default: {
        throw new Error('[buildDeleteManyRequest] No resource target.');
      }
    }

    // Please remove this if adding resource cases.
    // eslint-disable-next-line no-unreachable
    return { basePath, queryPayload, bodyPayload };
  } catch (error) {
    console.error('[buildDeleteManyRequest]:', error);
    throw error;
  }
}

/**
 * --------------------------------------------------------------------
 * Types and utilities for "getMany" data provider calls
 * Note: Currently no resources use getMany via ra data provider.
 * --------------------------------------------------------------------
 */

export type TGetManyParams = {
  ids: IdType[];
  meta?: TMetaParams | undefined;
};

export type TGetManyResult<RecordType extends SomeRecord = any> = {
  status: string | number; // not in ra, extended
  data: RecordType[]; // ra type defines this
};

export type TBuildGetManyRequest = {
  resource: TResourceTarget;
  params: TGetManyParams;
};

export type TBuildGetManyReqResult = {
  basePath: string;
  queryPayload: AnyRecordData;
  bodyPayload: AnyRecordData;
};

export function buildGetManyRequest({
  resource,
  params,
}: TBuildGetManyRequest): TBuildGetManyReqResult {
  try {
    let basePath = '';
    let queryPayload: AnyRecordData = {};
    let bodyPayload: AnyRecordData = {};

    const paramsIds = params?.ids;
    const validIds = Array.isArray(paramsIds) && paramsIds.length > 0;

    // IDs is a non-empty array
    if (!validIds) {
      throw new Error('[buildGetManyRequest] Missing ids array.');
    }

    switch (resource) {
      // Add resource cases as needed
      // case 'some/resource': {
      //   basePath = '/some/resource/';
      //   queryPayload = { ids: params.ids };
      //   break;
      // }
      default: {
        throw new Error('[buildGetManyRequest] No resource target.');
      }
    }

    // Please remove this if adding resource cases.
    // eslint-disable-next-line no-unreachable
    return { basePath, queryPayload, bodyPayload };
  } catch (error) {
    console.error('[buildGetManyRequest]:', error);
    throw error;
  }
}

/**
 * --------------------------------------------------------------------
 * Types and utilities for "getManyReference" data provider calls
 * Note: Currently no resources use getManyReference via ra data provider.
 * --------------------------------------------------------------------
 */

export type TGetManyReferenceParams = {
  target: string;
  id: IdType;
  pagination?: {
    page?: number | string;
    perPage?: number;
  };
  sort?: {
    field?: string;
    order?: string;
  };
  filter?: AnyRecordData;
  meta?: TMetaParams | undefined;
};

export type TGetManyReferenceResult<RecordType extends SomeRecord = any> = {
  status: string | number; // not in ra, extended
  data: RecordType[];
  total?: number;
  pageInfo?: {
    hasNextPage?: boolean;
    hasPreviousPage?: boolean;
  };
};

export type TBuildGetManyReferenceRequest = {
  resource: TResourceTarget;
  params: TGetManyReferenceParams;
};

export type TBuildGetManyReferenceReqResult = {
  basePath: string;
  queryPayload: AnyRecordData;
  bodyPayload: AnyRecordData;
};

export function buildGetManyReferenceRequest({
  resource,
  params,
}: TBuildGetManyReferenceRequest): TBuildGetManyReferenceReqResult {
  try {
    let basePath = '';
    let queryPayload: AnyRecordData = {};
    let bodyPayload: AnyRecordData = {};

    const paramsId = params?.id;
    const paramsTarget = params?.target;

    const validId = paramsId !== null && paramsId !== undefined && paramsId !== '';
    const validTarget =
      paramsTarget !== null && paramsTarget !== undefined && paramsTarget !== '';

    // ID and target are not null, undefined, or an empty string
    if (!validId || !validTarget) {
      throw new Error('[buildGetManyReferenceRequest] Missing valid id or target.');
    }

    switch (resource) {
      // Add resource cases as needed
      // case 'some/resource': {
      //   basePath = '/some/resource/';
      //   break;
      // }
      default: {
        throw new Error('[buildGetManyReferenceRequest] No resource target.');
      }
    }

    // Please remove this if adding resource cases.
    // eslint-disable-next-line no-unreachable
    return { basePath, queryPayload, bodyPayload };
  } catch (error) {
    console.error('[buildGetManyReferenceRequest]:', error);
    throw error;
  }
}

/**
 * --------------------------------------------------------------------
 * Types and utilities for "updateMany" data provider calls
 * Note: Currently no resources use updateMany via ra data provider.
 * --------------------------------------------------------------------
 */

export type TUpdateManyParams = {
  ids: IdType[];
  data: AnyRecordData;
  meta?: TMetaParams | undefined;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export type TUpdateManyResult<RecordType extends SomeRecord = any> = {
  status?: string | number;
  data?: IdType[];
};

export type TBuildUpdateManyRequest = {
  resource: TResourceTarget;
  params: TUpdateManyParams;
};

export type TBuildUpdateManyReqResult = {
  basePath: string;
  queryPayload: AnyRecordData;
  bodyPayload: AnyRecordData;
};

export function buildUpdateManyRequest({
  resource,
  params,
}: TBuildUpdateManyRequest): TBuildUpdateManyReqResult {
  try {
    let basePath = '';
    let queryPayload: AnyRecordData = {};
    let bodyPayload: AnyRecordData = {};

    const paramsIds = params?.ids;
    const validIds = Array.isArray(paramsIds) && paramsIds.length > 0;

    // IDs is a non-empty array
    if (!validIds) {
      throw new Error('[buildUpdateManyRequest] Missing ids array.');
    }

    switch (resource) {
      // Add resource cases as needed
      // case 'some/resource': {
      //   basePath = '/some/resource/';
      //   break;
      // }
      default: {
        throw new Error('[buildUpdateManyRequest] No resource target.');
      }
    }

    // Please remove this if adding resource cases.
    // eslint-disable-next-line no-unreachable
    return { basePath, queryPayload, bodyPayload };
  } catch (error) {
    console.error('[buildUpdateManyRequest]:', error);
    throw error;
  }
}
